/* eslint-disable no-underscore-dangle */
import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';
import classnames from 'classnames';
import { connect } from 'react-redux';
import React, { Fragment } from 'react';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import { ReactComponent as CircleLegend } from '../../../assets/images/circle.svg';
import {
    onChangeShowRotationFilter,
    onChangeOaRotationFilter,
    onChangeOaRotationMetric,
    onChangeOaRotationOperatorFilter,
} from '../../../ac/onChangeMapFilters';
import {
    RotationLegend,
    operationAreasRotationFilterValues,
    OaRotationMetricOptions,
    OaRotationOperatorTypes,
    OaRotationDevOperatorTypes,
    ADMIN,
    SUPER_ADMIN,
} from '../../../constants/variables';
import { LegendRenderer } from '../../LegendRenderer/LegendRenderer';
import {
    OPERATION_AREAS_ROTATION_GRADIENT_LEGEND,
    OA_TRIPS_GRADIENT_LEGEND,
    OA_FLEET_GRADIENT_LEGEND,
} from '../../../constants/legends';
import { MultiGroupFilter } from '../MultiGroupFilter';
import { withDisplayedLayersCheck } from '../../../HOC/withDisplayedLayersCheck';
import LayerFeatureSearch from '../../LayerFeatureSearch/LayerFeatureSearch';
import styles from '../FilterSidebar.module.css';
import { searchOperationAreaInTiles, highlightOperationArea, clearOperationAreaHighlight } from '../../../utils/searchOperationAreaInTiles';
import { StandardRadioGroup } from '../StandardRadioGroup';

// Карта метрик → конфиг легенды
const METRIC_LEGEND_MAP = {
    rotation: OPERATION_AREAS_ROTATION_GRADIENT_LEGEND,
    trips:    OA_TRIPS_GRADIENT_LEGEND,
    fleet:    OA_FLEET_GRADIENT_LEGEND,
};
const OaRotationFilterComponent = ({
    onChangeShowRotationFilter,
    onChangeOaRotationFilter,
    onChangeOaRotationMetric,
    onChangeOaRotationOperatorFilter,
    showRotationStatuses,
    oaRotationDays,
    oaRotationMetric,
    oaRotationAvailableOperators,
    oaRotationOperator,
    userGroups,
    layer,
}) => {
    const isExpanded = showRotationStatuses.includes(RotationLegend[0].value);
    const isDev = layer && layer.id === 'Операционные зоны dev';
    // ADMIN (не SUPER_ADMIN) видит только Whoosh
    const baseOperatorTypes = isDev ? OaRotationDevOperatorTypes : OaRotationOperatorTypes;
    let displayOperatorTypes = baseOperatorTypes;
    if (userGroups && userGroups.includes(ADMIN) && !userGroups.includes(SUPER_ADMIN)) {
        displayOperatorTypes = baseOperatorTypes.filter(o => o.value === 'whoosh');
    }
    const legendConfig = METRIC_LEGEND_MAP[oaRotationMetric] || OPERATION_AREAS_ROTATION_GRADIENT_LEGEND;

    return (
        <div>
            {/* Переиспользуемый компонент поиска */}
            <div style={{ padding: '10px' }}>
                <LayerFeatureSearch
                    searchFn={searchOperationAreaInTiles}
                    highlightFn={highlightOperationArea}
                    clearFn={clearOperationAreaHighlight}
                    placeholder={t`ID или Внутренний ID`}
                    notFoundMessage={t`Операционная зона не найдена в загруженных данных`}
                    successLabel={t`Найдена`}
                    searchLabel={t`Найти`}
                    resultFields={[ 'name', 'id', 'internalId', 'region' ]}
                />
            </div>

            {/* Главный тоггл "Статистики по зонам" */}
            <div className={styles.checkboxIconGrid}>
                {RotationLegend.map(({ value, color, label }) => (
                    <Fragment key={value}>
                        <CircleLegend
                            fill={color}
                            width="2vw"
                            height="2vw"
                            opacity="0.8"
                        />
                        <label htmlFor={label.id}>{i18n._(label)}</label>
                        <div>
                            <label className={styles.switch}>
                                <input
                                    type="checkbox"
                                    name="poi"
                                    id={value}
                                    checked={showRotationStatuses.includes(value)}
                                    value={value}
                                    onChange={onChangeShowRotationFilter}
                                />
                                <span className={classnames(styles.sliderRound, styles.slider)} />
                            </label>
                        </div>
                    </Fragment>
                ))}
            </div>

            {/* Панель настроек — показывается когда тоггл включён */}
            {isExpanded && (
                <>
                    {/* Выбор метрики: Rotation / Старты / Флот */}
                    <div className={styles.legHead}>{t`Метрика:`}</div>
                    <StandardRadioGroup
                        value={oaRotationMetric}
                        onChange={onChangeOaRotationMetric}
                    >
                        {OaRotationMetricOptions.map(({ value, label }) => (
                            <FormControlLabel
                                key={value}
                                className={styles.isochroneFilterLabel}
                                control={
                                    <Radio
                                        checked={oaRotationMetric === value}
                                        onChange={onChangeOaRotationMetric}
                                        value={value}
                                        className={classnames(
                                            styles.isochroneFilterInput, {
                                                [styles.checkedIsochroneFilterInput]: oaRotationMetric === value,
                                            }
                                        )}
                                        size="small"
                                    />
                                }
                                label={i18n._(label)}
                                labelPlacement="end"
                            />
                        ))}
                    </StandardRadioGroup>

                    {/* Период и динамический список операторов */}
                    <MultiGroupFilter
                        isVisible
                        groups={[
                            {
                                id: 'period',
                                title: t`Период:`,
                                value: String(oaRotationDays),
                                onChange: onChangeOaRotationFilter,
                                options: operationAreasRotationFilterValues.map(value => ({
                                    value,
                                    label: value === 1 ? t`${value} день` : t`${value} дней`,
                                })),
                            },
                        ]}
                        footer={null}
                    />

                    {/* Выбор оператора: радиопереключатель, динамически из тайлов */}
                    {displayOperatorTypes.length > 0 && (
                        <>
                            <div className={styles.legHead}>{t`Оператор:`}</div>
                            <StandardRadioGroup
                                value={oaRotationOperator}
                                onChange={onChangeOaRotationOperatorFilter}
                            >
                                {displayOperatorTypes
                                    .filter(({ value }) => oaRotationAvailableOperators.length === 0 || oaRotationAvailableOperators.includes(value))
                                    .map(({ value, label, fill }) => (
                                        <FormControlLabel
                                            key={value}
                                            className={styles.isochroneFilterLabel}
                                            control={
                                                <Radio
                                                    checked={oaRotationOperator === value}
                                                    onChange={onChangeOaRotationOperatorFilter}
                                                    value={value}
                                                    className={classnames(
                                                        styles.isochroneFilterInput, {
                                                            [styles.checkedIsochroneFilterInput]: oaRotationOperator === value,
                                                        }
                                                    )}
                                                    size="small"
                                                />
                                            }
                                            label={
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <CircleLegend
                                                        fill={fill}
                                                        width="12px"
                                                        height="12px"
                                                        opacity="0.9"
                                                    />
                                                    {i18n._(label)}
                                                </span>
                                            }
                                            labelPlacement="end"
                                        />
                                    ))
                                }
                            </StandardRadioGroup>
                        </>
                    )}

                    {/* Легенда выбранной метрики */}
                    <div style={{ marginTop: '8px' }}>
                        <LegendRenderer config={legendConfig} />
                    </div>
                </>
            )}
        </div>
    );
};
const mapStateToProps = ({
    mapFilters: {
        showRotationStatuses,
        oaRotationDays,
        oaRotationMetric,
        oaRotationAvailableOperators,
        oaRotationOperator,
    },
    loggedUser: { groups },
}) => ({
    showRotationStatuses,
    oaRotationDays,
    oaRotationMetric,
    oaRotationAvailableOperators,
    oaRotationOperator,
    userGroups: groups,
});
const ConnectedComponent = connect(mapStateToProps, {
    onChangeShowRotationFilter,
    onChangeOaRotationFilter,
    onChangeOaRotationMetric,
    onChangeOaRotationOperatorFilter,
})(OaRotationFilterComponent);
export const OaRotationFilter = withDisplayedLayersCheck(ConnectedComponent, t`Операционные зоны`);
export const OaRotationDevFilter = withDisplayedLayersCheck(ConnectedComponent, t`Операционные зоны dev`);
